// a react hook that fetches filtered tail numbers from the database in the vw_faa_tail_numbers

import * as React from "react";
import { useApi } from "../containers/ApiContainer";
import { Aircraft, FBO, Tenant } from "../types";
import { useDebouncedValue } from "./useDebouncedValue";
import { fetchAircraft } from "./api";
import { PostgrestClient } from "@supabase/postgrest-js";
import { stripHyphens } from "../utils/tailNumberUtils";

export const useFAATailNumbers = (
  filter: string,
  excludeTailNumbers: string[] = []
) => {
  const { postgrest } = useApi();

  // Debounce the filter to avoid too many queries
  const debouncedFilter = useDebouncedValue(() => filter, 300, [filter]);

  // Memoize the exclude list to avoid unnecessary re-renders
  const memoizedExcludeTailNumbers = React.useMemo(
    () => excludeTailNumbers,
    [excludeTailNumbers.join(",")]
  );

  // State management
  const [loading, setLoading] = React.useState(false);
  const [tailNumbers, setTailNumbers] = React.useState<Tenant[]>([]);

  // Server-side filtering with database query
  React.useEffect(() => {
    // Don't search for empty filters
    if (debouncedFilter.length < 1) {
      setTailNumbers([]);
      return;
    }

    const fetchTailNumbers = async () => {
      if (!postgrest) return;

      setLoading(true);
      try {
        // Build search patterns to handle hyphens flexibly
        const patterns = [debouncedFilter]; // Original input

        // If input doesn't have hyphen, try adding hyphen after first char (e.g., "CFWAP" -> "C-FWAP")
        if (!debouncedFilter.includes('-') && debouncedFilter.length > 1) {
          const withHyphen = `${debouncedFilter[0]}-${debouncedFilter.slice(1)}`;
          patterns.push(withHyphen);
        }

        // If input has hyphen, try without hyphen (e.g., "C-FWAP" -> "CFWAP")
        if (debouncedFilter.includes('-')) {
          const withoutHyphen = stripHyphens(debouncedFilter);
          patterns.push(withoutHyphen);
        }

        // Build OR query for multiple patterns
        const orConditions = patterns.map(p => `tail_number.ilike.${p}%`).join(',');

        const { data, error } = await postgrest
          .from("vw_faa_tail_numbers")
          .select("tail_number, aircraft_id")
          .or(orConditions)
          .limit(100)
          .order("tail_number", { ascending: true });

        if (error) {
          console.error("Error fetching FAA tail numbers:", error);
          setTailNumbers([]);
          return;
        }

        // Filter out excluded tail numbers
        const filteredData = (data || [])
          .filter((item) => !memoizedExcludeTailNumbers.includes(item.tail_number))
          .map((item) => ({
            aircraft_id: item.aircraft_id,
            tail_number: item.tail_number,
          })) as Tenant[];

        setTailNumbers(filteredData);
      } catch (error) {
        console.error("Error fetching FAA tail numbers:", error);
        setTailNumbers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTailNumbers();
  }, [debouncedFilter, memoizedExcludeTailNumbers, postgrest]);

  return {
    loading,
    tailNumbers,
  };
};

/**
 * Look up aircraft by tail number using the FAA tail numbers view
 * @param postgrest - PostgREST client
 * @param tailNumber - Tail number to look up
 * @param activeFBO - Active FBO for aircraft lookup
 * @returns Aircraft object or null if not found
 */
export const lookupAircraftByTailNumber = async (
  postgrest: PostgrestClient,
  tailNumber: string,
  activeFBO: FBO
): Promise<Aircraft | null> => {
  if (!postgrest || !tailNumber?.trim() || !activeFBO) {
    return null;
  }

  try {
    const trimmedTailNumber = tailNumber.trim();

    // Build search patterns to handle hyphens flexibly
    const patterns = [trimmedTailNumber]; // Original input

    // If input doesn't have hyphen, try adding hyphen after first char (e.g., "CFWAP" -> "C-FWAP")
    if (!trimmedTailNumber.includes('-') && trimmedTailNumber.length > 1) {
      const withHyphen = `${trimmedTailNumber[0]}-${trimmedTailNumber.slice(1)}`;
      patterns.push(withHyphen);
    }

    // If input has hyphen, try without hyphen (e.g., "C-FWAP" -> "CFWAP")
    if (trimmedTailNumber.includes('-')) {
      const withoutHyphen = stripHyphens(trimmedTailNumber);
      patterns.push(withoutHyphen);
    }

    // Try each pattern until we find a match
    for (const pattern of patterns) {
      const { data, error } = await postgrest
        .from("vw_faa_tail_numbers")
        .select("aircraft_id")
        .ilike("tail_number", pattern)
        .limit(1)
        .maybeSingle();

      if (!error && data?.aircraft_id) {
        // Use existing fetchAircraft helper to get full aircraft details (with caching)
        const aircraft = await fetchAircraft(postgrest, data.aircraft_id, activeFBO);
        return aircraft;
      }
    }

    return null;
  } catch (error) {
    console.error(`Failed to lookup aircraft for ${tailNumber}:`, error);
    return null;
  }
};
